/**
 * Refund Manager (Ronin Internal Tools).
 *
 * Driven by the global header, like the rest of the dashboard:
 *   - the header's ORGANIZATION scopes the table to that org's events;
 *   - the header's EVENT opens that event's refund settings. Clicking a row
 *     sets the header event, so the two always agree.
 *
 * The table answers "which events can take refunds right now, and how are
 * they doing". It does NOT change them: refunds are switched on and configured
 * on the event itself (Events > the event > Cashless Configuration), so there
 * is one place to edit and one place to watch. Every column is sortable, and
 * computed columns sort on their underlying value, not on display text.
 *
 * Two tabs: Settings, and ACH Payouts — that one is a worklist (download the
 * file, upload it to the bank, mark it sent) rather than a settings screen, so
 * it gets its own space.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import { ColDef, ICellRendererParams, RowClickedEvent } from "ag-grid-community";
import moment from "moment";
import { toast } from "react-hot-toast";
import { Link } from "react-router-dom";
import { Breadcrumbs } from "../../components/Breadcrumbs";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { useEventStore } from "../../store/event";
import { useOrganizationStore } from "../../store/organization";
import { REFUND_MANAGER_EVENTS } from "./queries";
import { AchPayoutsPanel } from "./AchPayoutsPanel";
import {
  RefundEvent,
  RefundState,
  STATE_LABEL,
  configOf,
  effective,
  money,
  refundsOpenAt,
  stateOf,
} from "./rules";

const client = createApolloClient();

type RequestRow = {
  event_id: number;
  status: string;
  amount: number;
  gross_amount: number;
  fee_amount: number;
  needs_manual_fix: boolean;
};

type Row = {
  event: RefundEvent;
  state: RefundState;
  feeAmount: number | null;
  minAmount: number | null;
  opensAt: Date | null;
  endDate: Date | null;
  sentCount: number;
  paidOut: number;
  feesKept: number;
  inFlight: number;
  attention: number;
};

// Sort order for the Refunds column: the ones that need a decision first.
const STATE_ORDER: Record<RefundState, number> = {
  open: 1,
  no_end_date: 2,
  not_open_yet: 3,
  off: 5,
  not_configured: 6,
};

const STATE_CLASSES: Record<RefundState, string> = {
  open: "bg-green-100 text-green-800",
  not_open_yet: "bg-blue-100 text-blue-800",
  no_end_date: "bg-amber-100 text-amber-800",
  off: "bg-gray-100 text-gray-600",
  not_configured: "bg-white text-gray-400 ring-1 ring-gray-200",
};

const dateComparator = (a: Date | null, b: Date | null) =>
  (a ? a.getTime() : -Infinity) - (b ? b.getTime() : -Infinity);

export const RefundManagerPage = () => {
  const headerEventId = useEventStore((s) => s.eventId);
  const setEventId = useEventStore((s) => s.setEventId);
  const allEvents = useEventStore((s) => s.allEvents);
  const organizationId = useOrganizationStore((s) => s.organizationId);

  const [events, setEvents] = useState<RefundEvent[]>([]);
  const [requests, setRequests] = useState<RequestRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState<"settings" | "ach">("settings");

  const ids = useMemo(
    () => (allEvents || []).map((e: { id: number }) => e.id).filter(Boolean),
    [allEvents]
  );

  const load = useCallback(async () => {
    if (!ids.length) {
      setEvents([]);
      setRequests([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await client.query({
        query: REFUND_MANAGER_EVENTS,
        variables: { ids },
        fetchPolicy: "network-only",
      });
      setEvents(data?.events || []);
      setRequests(data?.cashless_refund_requests || []);
    } catch (err) {
      toast.error(`Could not load events: ${(err as Error)?.message || err}`);
    } finally {
      setLoading(false);
    }
  }, [ids]);

  useEffect(() => {
    load();
  }, [load]);

  const rows: Row[] = useMemo(() => {
    const byEvent = new Map<number, RequestRow[]>();
    for (const r of requests) {
      if (!byEvent.has(r.event_id)) byEvent.set(r.event_id, []);
      byEvent.get(r.event_id)!.push(r);
    }
    return events.map((event) => {
      const config = configOf(event);
      const e = effective(config);
      const reqs = byEvent.get(event.id) || [];
      const sent = reqs.filter((r) => r.status === "sent");
      return {
        event,
        state: stateOf(event),
        feeAmount: config ? e.feeAmount : null,
        minAmount: config ? e.minAmount : null,
        opensAt: config ? refundsOpenAt(event) : null,
        endDate: event.end_date ? moment(event.end_date).toDate() : null,
        sentCount: sent.length,
        paidOut: sent.reduce((sum, r) => sum + (r.amount || 0), 0),
        feesKept: sent.reduce((sum, r) => sum + (r.fee_amount || 0), 0),
        inFlight: reqs.filter((r) => r.status === "processing").length,
        attention: reqs.filter((r) => r.needs_manual_fix).length,
      };
    });
  }, [events, requests]);

  const selected = rows.find((r) => r.event.id === headerEventId)?.event || null;

  const columnDefs: ColDef<Row>[] = useMemo(
    () => [
      {
        headerName: "Event",
        colId: "event",
        valueGetter: (p) => p.data?.event.name,
        flex: 2,
        minWidth: 200,
      },
      {
        headerName: "Refunds",
        colId: "state",
        valueGetter: (p) => (p.data ? STATE_ORDER[p.data.state] : null),
        width: 150,
        cellRenderer: (p: ICellRendererParams<Row>) => {
          if (!p.data) return null;
          return (
            <span
              className={`inline-flex rounded-full px-2 py-1 text-xs font-medium ${STATE_CLASSES[p.data.state]}`}
            >
              {STATE_LABEL[p.data.state]}
            </span>
          );
        },
        filterValueGetter: (p) => (p.data ? STATE_LABEL[p.data.state] : ""),
      },
      {
        headerName: "Event ends",
        colId: "endDate",
        valueGetter: (p) => p.data?.endDate ?? null,
        valueFormatter: (p) => (p.value ? moment(p.value).format("MMM D, YYYY") : "—"),
        comparator: dateComparator,
        width: 140,
      },
      {
        headerName: "Refunds open",
        colId: "opensAt",
        valueGetter: (p) => p.data?.opensAt ?? null,
        valueFormatter: (p) => (p.value ? moment(p.value).format("MMM D, YYYY h:mm A") : "—"),
        comparator: dateComparator,
        width: 190,
      },
      {
        headerName: "Ronin fee",
        colId: "fee",
        valueGetter: (p) => p.data?.feeAmount ?? null,
        valueFormatter: (p) => (p.value === null ? "—" : money(p.value)),
        width: 120,
      },
      {
        headerName: "Minimum",
        colId: "min",
        valueGetter: (p) => p.data?.minAmount ?? null,
        valueFormatter: (p) => (p.value === null ? "—" : money(p.value)),
        width: 120,
      },
      {
        headerName: "Refunds sent",
        colId: "sent",
        valueGetter: (p) => p.data?.sentCount ?? 0,
        width: 130,
      },
      {
        headerName: "Paid to attendees",
        colId: "paidOut",
        valueGetter: (p) => p.data?.paidOut ?? 0,
        valueFormatter: (p) => money(p.value),
        width: 160,
      },
      {
        headerName: "Fees kept",
        colId: "fees",
        valueGetter: (p) => p.data?.feesKept ?? 0,
        valueFormatter: (p) => money(p.value),
        width: 120,
      },
      {
        headerName: "In flight",
        colId: "inFlight",
        valueGetter: (p) => p.data?.inFlight ?? 0,
        width: 110,
      },
      {
        headerName: "Needs attention",
        colId: "attention",
        valueGetter: (p) => p.data?.attention ?? 0,
        width: 150,
        cellClass: (p) => (p.value > 0 ? "text-red-700 font-semibold" : ""),
      },
    ],
    []
  );

  const noScope = !ids.length;

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <Breadcrumbs />

      <div className="mt-4 sm:flex sm:items-end sm:justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Refund Manager</h1>
          <p className="mt-1 max-w-3xl text-sm text-gray-600">
            Turn cash refunds on per event, set Ronin's fee, the refund window and the bank
            account refunds are paid from. Pick the organization in the header; pick an event
            there or click a row to edit it.
          </p>
        </div>
        <a href="/cash-refunds/list" className="mt-3 text-sm font-medium text-primary-600 sm:mt-0">
          Refund queue →
        </a>
      </div>

      <div className="mt-6 border-b border-gray-200">
        <nav className="-mb-px flex gap-6" aria-label="Refund Manager sections">
          {(["settings", "ach"] as const).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              className={`whitespace-nowrap border-b-2 px-1 pb-3 text-sm font-medium ${
                tab === key
                  ? "border-primary-600 text-primary-700"
                  : "border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700"
              }`}
            >
              {key === "settings" ? "Settings" : "ACH Payouts"}
            </button>
          ))}
        </nav>
      </div>

      {noScope ? (
        <div className="mt-6 rounded-md border border-dashed border-gray-300 p-6 text-sm text-gray-600">
          {organizationId
            ? "This organization has no events you can see."
            : "Choose an organization in the header to see its events."}
        </div>
      ) : tab === "ach" ? (
        selected ? (
          <AchPayoutsPanel event={selected} />
        ) : (
          <div className="mt-6 rounded-md border border-dashed border-gray-300 p-4 text-sm text-gray-600">
            Choose an event in the header to see the refunds waiting to be paid.
          </div>
        )
      ) : (
        <>
          <div className="mt-6 rounded-md border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700">
            {selected ? (
              <>
                Refund settings for <strong>{selected.name}</strong> live on the event.{" "}
                <Link to={`/events/${selected.id}/edit`} className="link-underline">
                  Open it
                </Link>{" "}
                and use Cashless Configuration &gt; Cash Refunds Enabled.
              </>
            ) : (
              <>
                This page shows how every event's refunds are doing. To turn refunds on or change a
                fee, open the event and use Cashless Configuration &gt; Cash Refunds Enabled.
              </>
            )}
          </div>

          {loading && <p className="mt-6 text-sm text-gray-500">Loading events…</p>}
          <div className="ag-theme-quartz mt-6" style={{ height: 520, width: "100%" }}>
            <AgGridReact<Row>
              rowData={rows}
              columnDefs={columnDefs}
              getRowId={(p) => String(p.data.event.id)}
              // Every header sorts — house convention. Nothing below overrides it.
              defaultColDef={{ sortable: true, filter: true, resizable: true }}
              rowClassRules={{ "!bg-primary-50": (p) => p.data?.event.id === headerEventId }}
              onRowClicked={(ev: RowClickedEvent<Row>) =>
                ev.data && setEventId(ev.data.event.id)
              }
              rowStyle={{ cursor: "pointer" }}
            />
          </div>
        </>
      )}
    </div>
  );
};
